import { useMemo } from 'react';

import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { getNodes } from '@/utils/graphqlUtils';

import {
  CustomEntityGrowthCustomizationType,
  GrowthProfileModalGrowthOverride,
} from '../GrowthProfileModal/GrowthProfileModal.types';
import { getCustomEntityGrowthModalCustomizationType } from '../GrowthProfileModal/GrowthProfileModal.utils';
import { CustomEntityGrowthModalBody } from './CustomEntityGrowthModal.components';
import {
  CustomEntityGrowthFormShape,
  CustomEntityGrowthModalFormData,
} from './CustomEntityGrowthModal.types';
import { handleCustomEntityGrowthModalSubmit } from './CustomEntityGrowthModal.utils';
import {
  CustomEntityGrowthModal_EntityFragment,
  CustomEntityGrowthModal_ProposalFragment,
  useCustomEntityGrowthModalQuery,
} from './graphql/CustomEntityGrowthModal.generated';

export interface CustomEntityGrowthModalProps
  extends CustomEntityGrowthModalFormData {
  onClose: () => void;
  canDelete: boolean;
  onDelete: () => void;
  householdId: string;
  onSubmit: (arg: {
    formData: GrowthProfileModalGrowthOverride;
    entity?: CustomEntityGrowthModal_EntityFragment;
    proposal?: CustomEntityGrowthModal_ProposalFragment;
  }) => void;
  currentOverrideIds: string[];
}

export function CustomEntityGrowthModal({
  onClose,
  canDelete,
  onDelete,
  householdId,
  onSubmit: onSubmitExternal,
  currentOverrideIds,
  ...formData
}: CustomEntityGrowthModalProps) {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const formMethods = useForm<CustomEntityGrowthFormShape>({
    defaultValues: {
      entityOrProposalId: formData.entityOrProposalId ?? '',
      customGrowthRate: formData.customGrowthRate,
      entityFutureValuationValue: formData.entityFutureValuationValue,
      entityFutureValuationYear: formData.entityFutureValuationYear,
      entityGrowthAfterFutureValuation:
        formData.entityGrowthAfterFutureValuation,
      entityGrowthBeforeFutureValuation:
        formData.entityGrowthBeforeFutureValuation,
      customizationType:
        getCustomEntityGrowthModalCustomizationType(formData) ||
        CustomEntityGrowthCustomizationType.GrowthRate,
      kind: formData.kind ?? undefined,
    },
  });

  const { handleSubmit } = formMethods;

  const { data, loading } = useCustomEntityGrowthModalQuery({
    variables: { householdId },
    onError: (error) => {
      reportError(
        `Could not fetch list of household entities for ${householdId}`,
        error as Error
      );
      showFeedback(
        'Could not fetch the list of entities.  Please try again later.'
      );
    },
  });

  const entities: CustomEntityGrowthModal_EntityFragment[] = useMemo(
    () => getNodes(data?.entities),
    [data?.entities]
  );

  const proposals: CustomEntityGrowthModal_ProposalFragment[] = useMemo(
    () => getNodes(data?.proposals),
    [data?.proposals]
  );

  const onSubmit = handleSubmit((formData) => {
    try {
      const result = handleCustomEntityGrowthModalSubmit({
        formData,
        entities,
        proposals,
        onSubmit: onSubmitExternal,
      });

      if (result?.errorMessage) {
        showFeedback(result.errorMessage);
      }
    } catch (error) {
      showFeedback(
        'Could not update the growth profile for the selected entity.'
      );
      reportError(
        'Could not save custom entity growth profile',
        error as Error
      );
    }
  });

  return (
    <FormProvider {...formMethods}>
      <FormModal
        onClose={onClose}
        isOpen
        heading="Custom entity-level growth"
        actions={
          <FormModalActions.Provider>
            {canDelete && (
              <FormModalActions.DeleteButton onConfirmDelete={onDelete} />
            )}
            <FormModalActions.CancelButton onClick={onClose} />
            <FormModalActions.SaveButton
              onClick={onSubmit}
              disabled={loading}
            />
          </FormModalActions.Provider>
        }
      >
        <CustomEntityGrowthModalBody
          entities={entities}
          proposals={proposals}
          currentOverrideIds={currentOverrideIds}
          {...formData}
        />
      </FormModal>
    </FormProvider>
  );
}
